import { Component, OnInit } from '@angular/core';
import { DashboardService } from './dashboard.service';
import { NewsService } from '../news/news.service';
declare var html2pdf: any;

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit {
  newsCount = 0;
  categoriesCount = 0;
  publishedCount = 0;
  archivedCount = 0;
  isLoading = false;
  errorMessage = '';
  recentActivities: { title: string; category?: string; status?: string; date?: string }[] = [];
  averageDaysDiff: number | null = null;

  constructor(private dashboardService: DashboardService, private newsService: NewsService) {}

  ngOnInit(): void {
    this.loadSummary();
  }

  loadSummary(): void {
    this.isLoading = true;
    this.dashboardService.getSummary().subscribe({
      next: (res) => {
        const payload = res?.data ?? res ?? {};
        // map known API fields
        this.newsCount = payload.newsItems ?? payload.totalNews ?? payload.newsCount ?? this.newsCount;
        this.categoriesCount = payload.categories ?? payload.categoriesCount ?? payload.totalCategories ?? this.categoriesCount;
        this.publishedCount = payload.published ?? payload.publishedCount ?? this.publishedCount;
        this.archivedCount = payload.archived ?? payload.archivedCount ?? this.archivedCount;
        this.averageDaysDiff = typeof payload.averageDaysDiff === 'number' ? payload.averageDaysDiff : (payload.avgDaysDiff ?? null);

        // recent items from dashboard summary (preferred)
        const latest = payload.latestNews ?? payload.latest ?? payload.latest_news ?? null;
        if (Array.isArray(latest) && latest.length > 0) {
          this.recentActivities = latest.slice(0, 5).map((it: any) => ({
            title: it.title || it.name || 'Untitled',
            category: it.categoryName || it.category || '',
            status: it.status || '',
            date: it.publishDate || it.publish_date || it.date || ''
          }));
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load dashboard summary', err);
        this.errorMessage = 'Unable to load dashboard summary';
        this.isLoading = false;
      }
    });
  }

  loadRecentActivities(): void {
    this.newsService.getAllNews("").subscribe({
      next: (res) => {
        const list: any[] = res?.data ?? res ?? [];
        this.recentActivities = (Array.isArray(list) ? list : [])
          .slice(0, 5)
          .map(item => {
            const title = item?.translations?.find((t: any) => t.languageCode === 'en')?.title
              || item?.translations?.[0]?.title
              || item?.title
              || 'Untitled';
            const date = item?.publishDate || item?.publishDateTime || item?.publish_date || '';
            return { title, date };
          });
      },
      error: (err) => {
        console.error('Failed to load recent news', err);
        this.recentActivities = [];
      }
    });
  }

  exportReport(): void {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `dashboard-report-${timestamp}.pdf`;

    // Create HTML content for PDF with Khmer font support
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Battambang:wght@400;700&display=swap');
          body {
            font-family: 'Battambang', Khmer, Arial, sans-serif;
            margin: 20px;
            background-color: #fff;
            color: #333;
          }
          .header {
            background-color: #192147;
            color: white;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .header h1 {
            margin: 0;
            font-size: 24px;
          }
          .header h2 {
            margin: 5px 0;
            font-size: 18px;
          }
          .timestamp {
            font-size: 12px;
            color: #ccc;
            margin-top: 10px;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 16px;
            font-weight: bold;
            color: #192147;
            border-bottom: 2px solid #192147;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .metrics-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          .metric-card {
            border: 1px solid #e2e8f0;
            padding: 15px;
            border-radius: 8px;
            background-color: #f8fafc;
          }
          .metric-label {
            font-size: 12px;
            color: #64748b;
            text-transform: uppercase;
          }
          .metric-value {
            font-size: 20px;
            font-weight: bold;
            color: #192147;
            margin-top: 5px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
          }
          thead {
            background-color: #f1f5f9;
          }
          th, td {
            border: 1px solid #e2e8f0;
            padding: 12px;
            text-align: left;
            font-size: 12px;
          }
          th {
            font-weight: bold;
            color: #475569;
          }
          tbody tr:nth-child(even) {
            background-color: #f8fafc;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: bold;
          }
          .status-published {
            background-color: #d1fae5;
            color: #065f46;
          }
          .status-draft {
            background-color: #fef3c7;
            color: #92400e;
          }
          .status-archived {
            background-color: #e5e7eb;
            color: #374151;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 11px;
            color: #64748b;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ឯកសារលិខិតរបាយការណ៍ផ្ទាល់ខ្លួន</h1>
          <h2>Dashboard Summary Report</h2>
          <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
        </div>

        <div class="section">
          <div class="section-title">📊 Key Metrics</div>
          <div class="metrics-grid">
            <div class="metric-card">
              <div class="metric-label">News Items</div>
              <div class="metric-value">${this.newsCount}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Categories</div>
              <div class="metric-value">${this.categoriesCount}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Published</div>
              <div class="metric-value">${this.publishedCount}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">Archived</div>
              <div class="metric-value">${this.archivedCount}</div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📰 Recent News Items</div>
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Published Date</th>
              </tr>
            </thead>
            <tbody>
              ${this.recentActivities.length > 0 
                ? this.recentActivities.map(item => `
                <tr>
                  <td>${item.title || '-'}</td>
                  <td>${item.category || '-'}</td>
                  <td>
                    <span class="status-badge status-${item.status?.toLowerCase() || 'draft'}">
                      ${item.status || '-'}
                    </span>
                  </td>
                  <td>${item.date ? new Date(item.date).toLocaleDateString() : '-'}</td>
                </tr>
              `).join('')
                : '<tr><td colspan="4" style="text-align: center;">No recent news available</td></tr>'
              }
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>This report was automatically generated on ${new Date().toLocaleString()}</p>
          <p>Ministry of Tourism - Admin Portal</p>
        </div>
      </body>
      </html>
    `;

    // Generate PDF from HTML
    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    
    const opt = {
      margin: 10,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };

    html2pdf().set(opt).from(element).save();
  }
}
